import { isChangeOrigin } from '@tiptap/extension-collaboration'
import { Plugin, PluginKey, Transaction } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

export const INSERTION_PULSE_CLASS = 'insertion-pulse'

export const insertionPulsePluginKey = new PluginKey<DecorationSet>('insertionPulse')

type InsertionPulseMeta = { type: 'PULSE'; from: number; to: number } | { type: 'CLEAR' }

/** Pulses `from`–`to`, positions in the document `transaction` produces, once `transaction` is applied. */
export function pulseInsertedRange(transaction: Transaction, from: number, to: number): Transaction {
  return transaction.setMeta(insertionPulsePluginKey, { type: 'PULSE', from, to } satisfies InsertionPulseMeta)
}

export function clearInsertionPulse(transaction: Transaction): Transaction {
  return transaction.setMeta(insertionPulsePluginKey, { type: 'CLEAR' } satisfies InsertionPulseMeta)
}

/**
 * Highlights freshly inserted text with a CSS animation, and drops the highlight when that animation ends.
 */
export function createInsertionPulsePlugin(): Plugin<DecorationSet> {
  return new Plugin<DecorationSet>({
    key: insertionPulsePluginKey,

    state: {
      init() {
        return DecorationSet.empty
      },
      apply(transaction, decorations, _oldState, newState) {
        const meta = (transaction.getMeta(insertionPulsePluginKey) as InsertionPulseMeta | undefined) ?? null

        if (meta !== null) {
          return meta.type === 'PULSE' && meta.from < meta.to
            ? DecorationSet.create(newState.doc, [Decoration.inline(meta.from, meta.to, { class: INSERTION_PULSE_CLASS })])
            : DecorationSet.empty
        }

        if (!transaction.docChanged) {
          return decorations
        }

        // A synced change can rewrite the document wholesale, leaving no meaningful position to map the pulse onto.
        return isChangeOrigin(transaction) ? DecorationSet.empty : decorations.map(transaction.mapping, newState.doc)
      },
    },

    props: {
      decorations(state) {
        return insertionPulsePluginKey.getState(state) ?? DecorationSet.empty
      },
      handleDOMEvents: {
        animationend(view, event) {
          const isPulse = event.target instanceof HTMLElement && event.target.classList.contains(INSERTION_PULSE_CLASS)
          const isPulsing = (insertionPulsePluginKey.getState(view.state)?.find().length ?? 0) > 0

          if (isPulse && isPulsing) {
            view.dispatch(clearInsertionPulse(view.state.tr))
          }

          return false
        },
      },
    },
  })
}
